import {FieldError, FieldValues, get, Path, PathValue, useFormContext} from "react-hook-form";
import React, {ReactNode, useEffect, useState} from "react";
import {Button, Form} from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  CountrySelection,
  loadCountryData,
  loadStateData,
  StateSelection
} from "../../../util/CountryUtils";
import {InstituteSelection, loadInstitutes} from "../../../util/InstituteUtils";
import {InstituteSummary} from "../../../../application/model/InstituteSummary";
import {InstituteStatus} from "../../../../application/model/InstituteStatus";
import Dropdown from "./Dropdown";
import Text from "./Text";

/**
 * Props for this component
 */
interface InstituteProps<T extends FieldValues> {
  label: string;
  helpText?: ReactNode;
  defaultValue?: InstituteSelection;
  fieldId: Path<T>;
  disabled?: boolean;
  mandatory?: boolean;
  primary?: boolean;
  deleteInstitute?: () => void;
  allowNew?: boolean;
}

/**
 * This is a component that provides the form with the option to either select a pre-existing institute from a dropdown
 * or introduce a fully new institute.
 *
 * @param label The text to show before the text box. If this institute is marked as primary this text will be overwritten.
 * (By default this will show to the left of the input
 * @param helpText Subtext that shows on top of the question
 * @param defaultValue The default selection for this component
 * @param fieldId The ID of the field as defined in the form interface
 * @param disabled <i>Optional</i> A boolean to mark the field as disabled
 * @param mandatory <i>Optional</i> A boolean to mark the field as mandatory
 * @param primary <i>Optional</i> Marks this researcher institute as being for the primary institute
 * @param deleteInstitute <i>Optional</i> A function that can be used to delete this project institute entry
 * @param allowNew <i>Optional</i> A boolean to enable the option to pass in a new institute.
 */
export default function Institute<T extends Record<string, any>>({
                                                                   label,
                                                                   helpText,
                                                                   defaultValue,
                                                                   fieldId,
                                                                   disabled = false,
                                                                   mandatory = false,
                                                                   primary = false,
                                                                   deleteInstitute,
                                                                   allowNew = false
                                                                 }: InstituteProps<T>) {
  const [isNewInstitute, setIsNewInstitute] = useState<boolean>(false);
  const [listOfInstitutes, setListOfInstitutes] = useState<InstituteSummary[]>([]);
  const [listOfCountries, setListOfCountries] = useState<CountrySelection[]>([]);
  const [listOfStates, setListOfStates] = useState<StateSelection[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<CountrySelection | null>(null);
  const {formState: {errors}, setValue} = useFormContext<T>();

  // Work out what the paths for the specific form elements are
  const idPath = `${fieldId}.instituteId` as Path<T>;
  const namePath = `${fieldId}.newInstitute.name` as Path<T>;
  const countryPath = `${fieldId}.newInstitute.countryCode` as Path<T>;
  const countyPath = `${fieldId}.newInstitute.county` as Path<T>;
  const townPath = `${fieldId}.newInstitute.town` as Path<T>;
  const websitePath = `${fieldId}.newInstitute.website` as Path<T>;

  // Find errors
  const idError = get(errors, idPath) as FieldError | undefined;
  const nameError = get(errors, namePath) as FieldError | undefined;
  const countryError = get(errors, countryPath) as FieldError | undefined;
  const countyError = get(errors, countyPath) as FieldError | undefined;
  const townError = get(errors, townPath) as FieldError | undefined;
  const websiteError = get(errors, websitePath) as FieldError | undefined;

  useEffect(() => {
    if (!selectedCountry?.isoCode2) {
      setListOfStates([]);
      return;
    }

    loadStateData(selectedCountry.isoCode2)
    .then(states => setListOfStates(states));
  }, [selectedCountry]);

  const loadCountries = () => {
    loadCountryData().then((list) => setListOfCountries(list));
  }

  const loadStates = () => {
    const countryCode = selectedCountry?.isoCode2;
    if (!countryCode) {
      setListOfStates([]);
      return;
    }
    loadStateData(selectedCountry?.isoCode2).then(states => setListOfStates(states));
  }

  const loadListInstitutes = () => {
    loadInstitutes().then((institutes) => {
      setListOfInstitutes(institutes);
    })
  }

  return <Form.Group controlId={`formHorizontalInstitute`}>
    {helpText && <Row>
      <Col sm={2}></Col>
      <Col sm={10} className={"question-help-text"}>
        {helpText}
      </Col>
    </Row>}
    {isNewInstitute ? <>
      <Text<T>
        label={"Institute Name"}
        error={nameError}
        fieldId={namePath}
        mandatory
        sideAction={<>
          <Col sm={"auto"}>
            <Button variant="primary" onClick={() => setIsNewInstitute(false)}
                    style={{width: "100%"}}>
              Select Existing Institute
            </Button>
          </Col>
          {deleteInstitute && !primary && <Col sm={"auto"}>
            <Button variant="danger" onClick={deleteInstitute}><i
                className="bi bi-trash3"></i></Button>
          </Col>}
        </>}/>
      <Dropdown<T>
        label={"Country"}
        content={listOfCountries.map(c => ({
          key: c.isoCode2,
          label: c.name
        }))}
        lazyLoadTrigger={loadCountries}
        error={countryError}
        fieldId={countryPath}
        mandatory
        onChange={(value) => {
          setSelectedCountry(
            listOfCountries.find(c => c.isoCode2 === value) ?? null
          );
          setValue(countyPath, "" as PathValue<T, typeof countyPath>);
          loadStates();
        }}/>
      {(selectedCountry != null && selectedCountry.hasStates && selectedCountry.isoCode2 !== "GB") ?
        <Dropdown<T>
          label={"County/State"}
          content={listOfStates.map(s => ({
            key: selectedCountry?.isoCode2 === "US" ? s.code : s.name,
            label: s.name
          }))}
          error={countyError}
          fieldId={countyPath}
          mandatory
        />
        :
        <Text<T>
          label={"County/State"}
          error={countyError}
          fieldId={countyPath}
          mandatory/>
      }
      <Text<T>
        label={"Town/City"}
        error={townError}
        fieldId={townPath}
        mandatory/>
      <Text<T>
        label={"Website"}
        error={websiteError}
        fieldId={websitePath}
        mandatory/>
    </> : <>
      <Dropdown<T>
        label={!primary ? 'Institute Name' : label}
        content={listOfInstitutes.map(i => {
          const nonCompliant = i.status === InstituteStatus.NonCompliant.value;
          return {
            key: i.instituteId,
            label: nonCompliant ? `${i.name} (${InstituteStatus.NonCompliant.label})` : i.name,
            disabled: nonCompliant
          };
        })}
        lazyLoadTrigger={loadListInstitutes}
        error={idError}
        fieldId={idPath}
        defaultValue={defaultValue ?
          {
            key: defaultValue.id,
            label: defaultValue.name
          }
          : undefined}
        mandatory={mandatory}
        disabled={disabled}
        sideAction={<>
          {!disabled && allowNew && <Col sm={"auto"}>
            <Button variant="primary" onClick={() => setIsNewInstitute(true)}
                    style={{width: "100%"}}>
              <i className="bi bi-plus-circle"/> Add new institute
            </Button>
          </Col>}
          {!disabled && deleteInstitute && !primary && <Col sm={"auto"}>
            <Button variant="danger" onClick={deleteInstitute}><i
                className="bi bi-trash3"></i></Button>
          </Col>}
        </>}
      />
    </>}
  </Form.Group>
}
